import React, { useState, useEffect, useRef } from 'react';
import { Segmented, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from './api';
import SocialIcon from '../components/SocialIcon';

// Validated as a pair on the dashboard surface (#1C1927): CVD ΔE 17.3,
// normal-vision ΔE 24.6, both >= 3:1 contrast
const SERIES = [
  { key: 'view', color: '#9085e9' },
  { key: 'click', color: '#199e70' }
];
const DAYS = 30;

// The server groups by UTC day and skips empty days; lay out every day of
// the last 30 so gaps read as zero, not as missing
const buildDays = (daily) => {
  const counts = {};
  (daily || []).forEach(row => {
    counts[row._id.day] = counts[row._id.day] || { view: 0, click: 0 };
    counts[row._id.day][row._id.type] = row.count;
  });
  const days = [];
  const today = new Date();
  for (let i = DAYS - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i));
    const key = d.toISOString().slice(0, 10);
    days.push({ key, date: d, view: (counts[key] || {}).view || 0, click: (counts[key] || {}).click || 0 });
  }
  return days;
};

// Top of the y-axis: four equal whole-number steps of 1, 2 or 5 × 10^n
const niceMax = (value) => {
  const raw = Math.max(value, 4) / 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 5, 10].map(m => m * magnitude).find(n => n >= raw);
  return step * 4;
};

const formatNumber = (n, lang) => new Intl.NumberFormat(lang, { notation: n >= 10000 ? 'compact' : 'standard' }).format(n);

// Intl puts the sign where each language expects it: "%35,5" in Turkish,
// "35.5%" in English
const formatPercent = (ratio, lang) => new Intl.NumberFormat(lang, { style: 'percent', maximumFractionDigits: 1 }).format(ratio);

const LineChart = ({ days, lang, labels, empty }) => {
  const wrap = useRef(null);
  const [width, setWidth] = useState(600);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    const measure = () => wrap.current && setWidth(wrap.current.clientWidth);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const height = 220;
  const pad = { top: 12, right: 12, bottom: 26, left: 36 };
  const innerW = Math.max(width - pad.left - pad.right, 10);
  const innerH = height - pad.top - pad.bottom;
  const max = niceMax(Math.max(1, ...days.map(d => Math.max(d.view, d.click))));
  const ticks = [0, 1, 2, 3, 4].map(i => (max / 4) * i);
  const x = i => pad.left + (innerW * i) / (days.length - 1);
  const y = v => pad.top + innerH - (innerH * v) / max;
  const path = key => days.map((d, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(d[key]).toFixed(1)}`).join('');
  const dayLabel = (date, opts) => date.toLocaleDateString(lang, { timeZone: 'UTC', ...opts });

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const i = Math.round(((px - pad.left) / innerW) * (days.length - 1));
    setHover(Math.min(Math.max(i, 0), days.length - 1));
  };

  const hovered = hover !== null ? days[hover] : null;

  return (
    <div className="dash-chart" ref={wrap}>
      <svg width={width} height={height} role="img" aria-label={labels.title}
        onPointerMove={onMove} onPointerLeave={() => setHover(null)}>
        {ticks.map(tick => (
          <g key={tick}>
            <line x1={pad.left} x2={width - pad.right} y1={y(tick)} y2={y(tick)} className="dash-chart__grid" />
            <text x={pad.left - 8} y={y(tick)} dy="0.32em" textAnchor="end" className="dash-chart__tick">{formatNumber(tick, lang)}</text>
          </g>
        ))}
        {/* Weekly labels counted back from today; the last one ends at the
            right edge instead of centring past it */}
        {days.map((d, i) => (i % 7 === (days.length - 1) % 7) && (
          <text key={d.key} x={x(i)} y={height - 6} textAnchor={i === days.length - 1 ? 'end' : 'middle'} className="dash-chart__tick">{dayLabel(d.date, { day: 'numeric', month: 'short' })}</text>
        ))}
        <path d={`${path('view')}L${x(days.length - 1)},${y(0)}L${x(0)},${y(0)}Z`} fill={SERIES[0].color} opacity="0.1" />
        {SERIES.map(s => <path key={s.key} d={path(s.key)} fill="none" stroke={s.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />)}
        {hovered && <>
          <line x1={x(hover)} x2={x(hover)} y1={pad.top} y2={pad.top + innerH} className="dash-chart__crosshair" />
          {SERIES.map(s => <circle key={s.key} cx={x(hover)} cy={y(hovered[s.key])} r="4.5" fill={s.color} stroke="#1C1927" strokeWidth="2" />)}
        </>}
      </svg>
      {empty && <div className="dash-chart__empty"><span>{empty}</span></div>}
      {hovered && !empty &&
        <div className="dash-chart__tooltip" style={{ left: Math.min(Math.max(x(hover), 70), width - 70) }}>
          <div className="dash-chart__tooltip-date">{dayLabel(hovered.date, { weekday: 'short', day: 'numeric', month: 'long' })}</div>
          {SERIES.map(s => (
            <div key={s.key} className="dash-chart__tooltip-row">
              <i style={{ background: s.color }} /><strong>{formatNumber(hovered[s.key], lang)}</strong><span>{labels[s.key]}</span>
            </div>
          ))}
        </div>}
    </div>
  );
};

const Stat = ({ label, value, sub }) => (
  <div className="dash-stat">
    <span className="dash-stat__label">{label}</span>
    <span className="dash-stat__value">{value}</span>
    {sub && <span className="dash-stat__sub">{sub}</span>}
  </div>
);

// A ranked list with a thin bar showing each row's share of the top row
const BarList = ({ rows, empty }) => {
  if (!rows.length) return <p className="dash-muted">{empty}</p>;
  const top = Math.max(...rows.map(r => r.count), 1);
  return (
    <ul className="dash-barlist">
      {rows.map(row => (
        <li key={row.key}>
          <span className="dash-barlist__label">{row.icon}{row.label}</span>
          <span className="dash-barlist__value">{row.value}</span>
          <span className="dash-barlist__track"><span style={{ width: `${(row.count / top) * 100}%` }} /></span>
        </li>
      ))}
    </ul>
  );
};

const AnalyticsPanel = ({ user }) => {
  const [t, i18n] = useTranslation();
  const lang = i18n.language.slice(0, 2) === 'en' ? 'en' : 'tr';
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [view, setView] = useState('chart');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setFailed(false);
    api.analytics(user.username).then(setData).catch(() => setFailed(true));
  }, [user.username, attempt]);

  const head = (
    <header className="dash-panel__head">
      <h1>{t('dash.nav.analytics')}</h1>
      <span className="dash-muted">{t('dash.last30')}</span>
    </header>
  );

  if (failed) {
    return (
      <div className="dash-panel">
        {head}
        <div className="dash-card dash-state" role="alert">
          <p className="dash-state__title">{t('dash.analyticsFailed')}</p>
          <Button icon={<ReloadOutlined />} onClick={() => setAttempt(n => n + 1)}>{t('dash.retry')}</Button>
        </div>
      </div>
    );
  }

  // Same boxes as the loaded page, so nothing shifts when the numbers land
  if (!data) {
    return (
      <div className="dash-panel" aria-busy="true">
        {head}
        <div className="dash-stats">{[0, 1, 2].map(i => <i key={i} className="dash-stat dash-skeleton__block" style={{ height: 104 }} />)}</div>
        <i className="dash-card dash-skeleton__block" style={{ height: 300 }} />
      </div>
    );
  }

  const days = buildDays(data.daily);
  const views30 = days.reduce((s, d) => s + d.view, 0);
  const clicks30 = days.reduce((s, d) => s + d.click, 0);
  const ctr = views30 ? clicks30 / views30 : 0;
  const fmt = n => formatNumber(n, lang);
  const devicesTotal = (data.devices || []).reduce((s, d) => s + d.count, 0) || 1;
  const linkTypes = {};
  user.links.forEach(link => { linkTypes[link._id] = link.type; });

  return (
    <div className="dash-panel">
      {head}

      <div className="dash-stats">
        <Stat label={t('dash.views')} value={fmt(views30)} sub={t('dash.allTime', { count: fmt(data.totalViews) })} />
        <Stat label={t('dash.clicks')} value={fmt(clicks30)} sub={t('dash.allTime', { count: fmt(data.totalClicks) })} />
        {/* No views means no rate, not a rate of zero */}
        <Stat label={t('dash.ctr')} value={views30 ? formatPercent(ctr, lang) : '—'} sub={t('dash.ctrHint')} />
      </div>

      <section className="dash-card dash-section">
        <div className="dash-section__head">
          <h2>{t('dash.daily')}</h2>
          <div className="dash-legend">
            {SERIES.map(s => <span key={s.key}><i style={{ background: s.color }} />{t(s.key === 'view' ? 'dash.views' : 'dash.clicks')}</span>)}
          </div>
          <Segmented size="small" value={view} onChange={setView}
            options={[{ value: 'chart', label: t('dash.chart') }, { value: 'table', label: t('dash.table') }]} />
        </div>
        {view === 'chart'
          ? <LineChart days={days} lang={lang} labels={{ title: t('dash.daily'), view: t('dash.views'), click: t('dash.clicks') }}
              empty={views30 + clicks30 === 0 ? t('dash.chartEmpty') : null} />
          : <div className="dash-table-wrap">
              <table className="dash-table">
                <thead><tr><th>{t('dash.date')}</th><th>{t('dash.views')}</th><th>{t('dash.clicks')}</th></tr></thead>
                <tbody>
                  {[...days].reverse().map(d => (
                    <tr key={d.key}><td>{d.date.toLocaleDateString(lang, { timeZone: 'UTC', day: 'numeric', month: 'long' })}</td><td>{fmt(d.view)}</td><td>{fmt(d.click)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>}
      </section>

      <div className="dash-two">
        {/* Per-link counts are the lifetime counters on each link, not the
            last 30 days like everything else here; the server keeps no
            30-day per-link figure yet, so the card says so plainly */}
        <section className="dash-card dash-section">
          <div className="dash-section__head">
            <h2>{t('dash.topLinks')}</h2>
            <span className="dash-tag dash-tag--range">{t('dash.allTimeRange')}</span>
          </div>
          <BarList empty={t('dash.noData')}
            rows={[...data.links].filter(l => l.clicks > 0).sort((a, b) => b.clicks - a.clicks).slice(0, 10).map(l => ({
              key: l._id, label: l.linkTitle, icon: <SocialIcon type={linkTypes[l._id]} />, count: l.clicks, value: fmt(l.clicks)
            }))} />
        </section>
        <section className="dash-card dash-section">
          <div className="dash-section__head">
            <h2>{t('dash.sources')}</h2>
            <span className="dash-tag dash-tag--range">{t('dash.last30')}</span>
          </div>
          <BarList empty={t('dash.noData')}
            rows={(data.referrers || []).map(r => ({ key: r.host || 'direct', label: r.host || t('dash.direct'), count: r.count, value: fmt(r.count) }))} />
          <h2 className="dash-section__sub">{t('dash.devices')}</h2>
          <BarList empty={t('dash.noData')}
            rows={(data.devices || []).map(d => ({
              key: d.device, label: t(`dash.device.${d.device}`, { defaultValue: d.device }), count: d.count, value: formatPercent(Math.round((d.count / devicesTotal) * 100) / 100, lang)
            }))} />
        </section>
      </div>
    </div>
  );
};

export default AnalyticsPanel;
